import type { Directive } from 'vue'
import { useScroll } from '@vueuse/core'
import { ref } from 'vue'

interface ShadowScrollElement extends HTMLElement {
  _shadowScrollCleanup?: () => void
  _shadowScrollVueUse?: any
}

const ARShadowScroll: Directive<ShadowScrollElement> = {
  mounted(el: ShadowScrollElement) {
    // Create a ref for the element to work with VueUse
    const elementRef = ref(el)

    // Use VueUse's useScroll composable
    const { arrivedState, y } = useScroll(elementRef)

    // Store VueUse instance for cleanup
    el._shadowScrollVueUse = { arrivedState }

    const updateShadowClasses = () => {
      const scrollHeight = el.scrollHeight
      const clientHeight = el.clientHeight

      // Check if element has vertical scrollbars
      const hasVerticalScrollbars = scrollHeight > clientHeight

      if (!hasVerticalScrollbars) {
        // Remove all shadow classes if no scrollbars
        el.classList.remove('shadow-inner-up', 'shadow-inner-down', 'shadow-inner-y')
        return
      }
      else {
        el.classList.add('overflow-y-scroll')
      }

      // Remove existing classes
      el.classList.remove('shadow-inner-up', 'shadow-inner-down', 'shadow-inner-y')

      // Use manual threshold calculation for precise control
      const scrollTop = y.value || 0
      const maxScroll = scrollHeight - clientHeight
      const tolerance = 15 // Threshold for top/bottom detection

      const isAtTop = scrollTop <= tolerance
      const isAtBottom = scrollTop >= maxScroll - tolerance

      // Apply classes based on scroll position
      if (isAtTop && !isAtBottom) {
        // At top only - no top shadow, show bottom shadow
        el.classList.add('shadow-inner-up')
      }
      else if (isAtBottom && !isAtTop) {
        // At bottom only - show top shadow, no bottom shadow
        el.classList.add('shadow-inner-down')
      }
      else if (!isAtTop && !isAtBottom) {
        // In middle - show both shadows
        el.classList.add('shadow-inner-y')
      }
    }

    // Watch for changes in arrivedState
    let rafId: number | null = null
    const handleScrollUpdate = () => {
      if (rafId) {
        cancelAnimationFrame(rafId)
      }
      rafId = requestAnimationFrame(updateShadowClasses)
    }

    // Initial update
    updateShadowClasses()

    // Add scroll listener for updates
    el.addEventListener('scroll', handleScrollUpdate, { passive: true })

    // Store cleanup function
    el._shadowScrollCleanup = () => {
      el.removeEventListener('scroll', handleScrollUpdate)
      if (rafId) {
        cancelAnimationFrame(rafId)
      }
      // Remove all shadow classes
      el.classList.remove('shadow-inner-up', 'shadow-inner-down', 'shadow-inner-y')
      // Clean up VueUse reference
      delete el._shadowScrollVueUse
    }
  },

  unmounted(el: ShadowScrollElement) {
    if (el._shadowScrollCleanup) {
      el._shadowScrollCleanup()
      delete el._shadowScrollCleanup
    }
  },
}

export { ARShadowScroll }
